import { TelegramLoginButton } from "../components/TelegramLoginButton";
import { env } from "../env";
import type { TelegramUser } from "../types";
import { Shield, Smartphone, Database, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

interface LoginProps {
  user: TelegramUser | null;
  onLogin: (user: TelegramUser) => void;
}

export const Login = ({ user, onLogin }: LoginProps) => {
  const navigate = useNavigate();

  // Если пользователь уже авторизован, перенаправляем на профиль
  useEffect(() => {
    if (user) {
      navigate("/profile", { replace: true });
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  const benefits = [
    {
      icon: <Shield className="text-primary" size={20} />,
      text: "Два способа входа",
      description: "Telegram или Яндекс ID — выберите удобный"
    },
    {
      icon: <Smartphone className="text-accent-foreground" size={20} />,
      text: "Прогресс на всех устройствах",
      description: "Решения и результаты синхронизируются автоматически"
    },
    {
      icon: <Database className="text-primary" size={20} />,
      text: "Персональный роадмап",
      description: "AI строит план обучения под ваш уровень и цели"
    },
    {
      icon: <CheckCircle className="text-accent-foreground" size={20} />,
      text: "AI Tutor на каждой странице",
      description: "Помощь и объяснения в тренажёре, курсах и чате"
    },
  ];

  return (
    <div className="min-h-[calc(100vh-6rem)] bg-gradient-to-b from-neutral-light to-neutral-bg flex items-center justify-center p-4 py-12">
      <div className="max-w-5xl w-full grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
        {/* Левая часть - информация и преимущества */}
        <div className="space-y-6 animate-fadeIn order-1 md:order-1">
          {/* Заголовок */}
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-neutral-text mb-4 text-center md:text-left">
              Начните учиться
            </h1>
            <p className="text-lg text-neutral-text/80 font-body mb-2 text-center md:text-left">
              Войдите через Telegram или Яндекс ID
            </p>
            <p className="text-sm text-neutral-text/60 font-body text-center md:text-left">
              Персональный роадмап, интерактивный тренажёр и AI Tutor — всё в одном месте
            </p>
          </div>

          {/* Преимущества - скрыты на мобильной версии, показываются отдельно ниже */}
          <div className="hidden md:block space-y-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-start gap-4 p-4 bg-neutral-bg rounded-lg border border-neutral-border hover:border-primary/30 transition-colors"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-heading font-medium text-neutral-text mb-1">
                    {benefit.text}
                  </h3>
                  <p className="text-sm text-neutral-text/70 font-body">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Правая часть - форма авторизации */}
        <div className="animate-fadeIn order-2 md:order-2" style={{ animationDelay: "0.1s" }}>
          <TelegramLoginButton
            botUsername={env.TELEGRAM_BOT_USERNAME}
            user={user}
            onLogin={onLogin}
          />
        </div>

        {/* Преимущества для мобильной версии - показываются после формы */}
        <div className="md:hidden space-y-4 mt-8 order-3">
          {benefits.map((benefit, index) => (
            <div 
              key={index} 
              className="flex items-start gap-4 p-4 bg-neutral-bg rounded-lg border border-neutral-border hover:border-primary/30 transition-colors"
            >
              <div className="flex-shrink-0 mt-0.5">
                {benefit.icon}
              </div>
              <div>
                <h3 className="font-heading font-medium text-neutral-text mb-1">
                  {benefit.text}
                </h3>
                <p className="text-sm text-neutral-text/70 font-body">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn { 
            animation: fadeIn 0.6s ease-out forwards; 
            opacity: 0;
          }
        `}
      </style>
    </div>
  );
};
